import { errors } from "../copy/errors.js";

export interface ApiErrorBody {
  error: string;
  message: string;
}

let csrfMemo: string | null = null;

function csrfToken(): string | null {
  if (csrfMemo) return csrfMemo;
  const match = document.cookie.match(/(?:^|; )vf_csrf=([^;]*)/);
  return match ? decodeURIComponent(match[1] as string) : null;
}

/** The `code` of an ApiError when the request got no answer at all. */
export const OFFLINE = "offline";

/**
 * No usable answer: no connection, or the worker or Cloudflare failing. The
 * device then goes on with what it kept (issues #118, #120). A 4xx is an
 * answer, and a real one: a session that ended, a list that is gone.
 */
export function noAnswer(err: unknown): boolean {
  return err instanceof ApiError && (err.code === OFFLINE || err.code === "bad_response" || err.status >= 500);
}

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    const csrf = csrfToken();
    if (csrf) headers.set("X-VF-CSRF", csrf);
  }
  // FormData sets its own multipart boundary; overriding it breaks the upload.
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  let response: Response;
  try {
    response = await fetch(path, { ...init, headers, credentials: "include" });
  } catch {
    // No answer at all: no network, or it broke on the way (issue #118).
    throw new ApiError(0, OFFLINE, errors.offline);
  }
  const headerCsrf = response.headers.get("X-VF-CSRF");
  if (headerCsrf) csrfMemo = headerCsrf;
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  let data: T | ApiErrorBody;
  try {
    data = text ? (JSON.parse(text) as T | ApiErrorBody) : ({} as T);
  } catch {
    // Not ours: Cloudflare's own HTML page, e.g. after its 100 s limit (#82).
    throw new ApiError(response.status, "bad_response", errors.failed);
  }
  if (!response.ok) {
    const body = data as ApiErrorBody;
    throw new ApiError(response.status, body.error ?? "error", body.message ?? errors.failed);
  }
  return data as T;
}

export async function ensureCsrf(): Promise<void> {
  // Offline the app still starts (issue #118); the next request tries again.
  await fetch("/api/health", { credentials: "include" }).catch(() => undefined);
}
